import {
  Alert,
  Button,
  Col,
  ConsoleCard,
  Form,
  Input,
  Modal,
  Page,
  PageHeading,
  Row,
  Select,
} from 'admin-web-ui'
import { notification } from 'antd'
import React, { useState } from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { useProjectContext } from '../../../containers/Project'
import { getUserByUsername } from '../../UserLookup/helpers'
import { resetUserVaults } from './helpers'
const { useForm } = Form

const StyledInput = styled(Input)`
  flex-grow: 1;
  ::placeholder {
    text-transform: capitalize;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const StyledButton = styled(Button)`
  min-width: 112px;
`

const SubmitButton = styled(StyledButton)`
  align-self: flex-start;
`

const ConfirmButton = styled(StyledButton)`
  margin-left: 8px;
`

const StyledAlert = styled(Alert)`
  margin-top: 24px;
`

const FormContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const FormSelect = styled(Form.Item)`
  width: 200px;
`

const FormInput = styled(Form.Item)`
  flex-grow: 1;
  margin-left: 24px;
`

export const VaultManagement: React.FC = () => {
  const { projectClient } = useProjectContext()
  const [identifierType, setIdentifierType] = useState<string | undefined>()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [isErrorCleared, setIsErrorCleared] = useState<boolean>(true)
  const [form] = useForm()

  const openNotification = (): void => {
    notification.open({
      message: 'Secure Vault Service registration successfully reset!',
      description:
        'The user will need to set a new master password and download an updated Rescue Kit containing their new secret code.',
    })
  }

  const [submitState, submit] = useAsyncFn(async () => {
    const value = form.getFieldValue('value')

    try {
      const sub =
        identifierType === 'platform username'
          ? (await getUserByUsername(projectClient, value)).sub
          : value

      await resetUserVaults(projectClient, sub)
    } catch (e) {
      setIsErrorCleared(false)
      throw e
    }

    setModalOpen(false)
    form.resetFields()
    openNotification()
  }, [identifierType, form, projectClient])

  return (
    <Page>
      <PageHeading>Vault Management</PageHeading>
      <Row>
        <Col span={24}>
          <ConsoleCard>
            <Form
              form={form}
              onFinish={() => setModalOpen(true)}
              layout="vertical"
            >
              <ConsoleCard.Heading>Reset User Vaults</ConsoleCard.Heading>
              <FormContainer>
                <FormSelect
                  name="identifierType"
                  label="User Identifier Type:"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an identifier type',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select an identifier type"
                    onChange={(option: string) => setIdentifierType(option)}
                  >
                    <Select.Option key={'1'} value={'platform username'}>
                      Platform Username
                    </Select.Option>
                    <Select.Option key={'2'} value={'platform subject'}>
                      Platform Subject
                    </Select.Option>
                  </Select>
                </FormSelect>
                <FormInput
                  name="value"
                  label="Value"
                  rules={[
                    {
                      required: true,
                      message: `Please input a value`,
                    },
                  ]}
                >
                  <StyledInput placeholder={identifierType} />
                </FormInput>
              </FormContainer>
              <SubmitButton
                type="primary"
                shape="round"
                loading={submitState.loading}
                htmlType="submit"
              >
                Reset
              </SubmitButton>
            </Form>
          </ConsoleCard>
        </Col>
      </Row>
      <Modal
        open={modalOpen}
        title="Are you sure you want to reset vaults for this user?"
        footer={null}
      >
        <ModalFooter>
          <StyledButton
            type="default"
            shape="round"
            onClick={() => {
              setModalOpen(false)
              setIsErrorCleared(true)
            }}
          >
            Cancel
          </StyledButton>
          <ConfirmButton
            type="primary"
            shape="round"
            loading={submitState.loading}
            onClick={submit}
          >
            Confirm
          </ConfirmButton>
        </ModalFooter>
        {submitState.error && !isErrorCleared ? (
          <StyledAlert
            message={submitState.error.message}
            type="error"
            banner
            closable
          />
        ) : null}
      </Modal>
    </Page>
  )
}
